import {
  FunctionComponent,
  RefObject,
  createRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WasteDetailsTemplate } from '../../wasteTemplate/WasteDetailsTemplate/WasteDetailsTemplate';
import { SelectInput } from '@organisms/SelectInput';
import { RadioOptions } from '@molecules/RadioOptions';
import { useWasteStore } from '@waste/store/store';
import {
  subCategories,
  questions,
  fieldOptions,
  mainCategories,
  isRecurringIssueQuestionData,
} from '@waste/db/db';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@waste/routes/Routes';
import { ErrorSummary } from '@atoms/error-banner';
import {
  wasteCurrentSubcategory,
  validateDynamicFields,
  inferCategoryValidation,
  textAreaInputSchema,
} from '@utils';
import { errorFormatter, ErrorMessageProps } from '@utils';
import { Label } from '@atoms/label';
import { TextAreaTemplate } from '@templates';

interface CategoryQuestionProps {
  fieldType: string;
  key: string;
  name: string;
  parentSubcategories: string[];
  response: string;
}

interface WasteCategoryQuestionsAndOptionsProps {
  response: string;
  parentQuestion: string;
  options: string[];
}

export const WasteDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessageProps[]>([]);
  const [errorPaths, setErrorPaths] = useState<string[]>([]);

  const resetErrorMessages = () => {
    setErrorMessages([]);
    setErrorPaths([]);
  };

  const {
    filename,
    formattedInferCategory,
    currentSubcategory,
    updateCurrentSubcategory,
    isImageMismatch,
    updateIsImageMismatch,
    wasteCategory,
    selectedOptionRef,
    updateSelectedOptionRef,
    updateSurfacedetails,
    updateActualMeasurementDetails,
    updateOffensive,
    selectedSubcategory,
    updateSelectedSubcategory,
    renderedWasteCategoryQuestionsAndOptions,
    updateRenderedWasteCategoryQuestionsAndOptions,
    surfaceDetails,
    isNavigate,
    updateIsNavigate,
    actualMeasurementDetails,
    isRecurringIssue,
    updateIsRecurringIssue,
    whenRecurringHappened,
    updateWhenRecurringHappened,
  } = useWasteStore();

  const [
    wasteCategoryQuestionsAndOptions,
    setWasteCategoryQuestionsAndOptions,
  ] = useState<WasteCategoryQuestionsAndOptionsProps[]>([]);

  // selected option of the inferImageCategory and wasteCategory.
  // only used in this component and not part of the payload: don't move to zustand.
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [isShowWhenHappenedField, showIsShowWhenHappenedField] =
    useState<boolean>(false);
  const extraQuestionData = isRecurringIssueQuestionData;

  const findSubcategories = () => {
    const filteredSubcategories = subCategories.filter(
      (subcategory) =>
        subcategory.parentCategory.toLowerCase() ===
        wasteCategory.toLowerCase(),
    );
    updateCurrentSubcategory(filteredSubcategories);
  };

  const findFieldOptions = (
    questions: CategoryQuestionProps[],
    currentSubcategory = '',
  ) => {
    const categoryQuestions = questions.map(
      (question: CategoryQuestionProps) => {
        const filteredOptions = fieldOptions.filter(
          (option) =>
            option.parentQuestion.toLowerCase() === question.name.toLowerCase(),
        );

        if (
          currentSubcategory.toLowerCase() ===
            'damaged, dirty or missing bin' &&
          question.name.toLowerCase() === 'what type of bin is it?'
        ) {
          return [
            {
              parentQuestion: 'What type of bin is it?',
              options: filteredOptions[0].options
                .slice(0, 4)
                .sort((a, b) => a.localeCompare(b)),
              response: question.response,
            },
          ];
        }

        if (
          currentSubcategory.toLowerCase() === 'full bin' &&
          question.name.toLowerCase() === 'what type of bin is it?'
        ) {
          return [
            {
              parentQuestion: 'What type of bin is it?',
              options: filteredOptions[0].options
                .slice(3)
                .sort((a, b) => a.localeCompare(b)),
              response: question.response,
            },
          ];
        }

        return filteredOptions.map((filteredOption) => {
          return {
            parentQuestion: filteredOption.parentQuestion,
            options: filteredOption.options,
            response: question.response,
          };
        });
      },
    );

    setWasteCategoryQuestionsAndOptions(categoryQuestions.flat());
    resetErrorMessages();
    if (!isNavigate) {
      updateRenderedWasteCategoryQuestionsAndOptions(
        categoryQuestions[0].flat(),
      );
    }
  };

  const findQuestions = () => {
    const filteredQuestions = questions.filter((question) =>
      question.parentSubcategories.includes(currentSubcategory![0].name),
    );

    if (filteredQuestions.length === 0) return; // to handle subcategories with no questions (Ex. Litter, Dog poo etc.)

    findFieldOptions(filteredQuestions);
  };

  const formatSelectedOption = (value: string) => {
    return value
      .toLowerCase()
      .replace(/,| /g, (match) => (match === ',' ? '' : '_'));
  };

  const handleInferCategoryOptionChange = (value: string) => {
    resetErrorMessages();

    try {
      // set the selected option
      setSelectedOption(value);
      updateSelectedOptionRef(value);
      updateSelectedSubcategory('');
      // Clear questions rendered for a *different* category so stale options
      // don't linger after switching. Skip when re-selecting the same category
      // (e.g. the restore click fired on back-navigation) so saved answers
      // survive and the page restores correctly.
      if (value !== selectedOptionRef) {
        updateRenderedWasteCategoryQuestionsAndOptions(undefined);
        // Reset category-specific answers so a stale value can't resurface
        // via a SelectInput placeholder after switching categories.
        updateSurfacedetails('');
        updateActualMeasurementDetails('');
      }
      const formattedValue = formatSelectedOption(value);
      const newSubCategory = subCategories.filter(
        (subCategory) => subCategory.parentCategory === formattedValue,
      );
      updateCurrentSubcategory(newSubCategory);
    } catch (error) {
      throw new Error();
    }
  };

  const handleSubcategoryOptionChange = (value: string) => {
    resetErrorMessages();
    const filteredQuestions = questions.filter((question) =>
      question.parentSubcategories.includes(value),
    );

    if (
      value === 'Damaged, dirty or missing bin' &&
      renderedWasteCategoryQuestionsAndOptions !== undefined &&
      renderedWasteCategoryQuestionsAndOptions!.length > 1
    ) {
      return;
    }

    updateSelectedSubcategory(value);

    const graffitiFamily = ['Graffiti', 'Posters', 'Stickers'];
    // Rebuild questions on every subcategory change, except when switching
    // within the graffiti family (Graffiti/Posters/Stickers share the same
    // questions, so preserve any answers already given).
    const isSwitchingWithinGraffitiFamily =
      graffitiFamily.includes(value) &&
      graffitiFamily.includes(selectedSubcategory);
    if (!isSwitchingWithinGraffitiFamily) {
      findFieldOptions(filteredQuestions, value);
    }
  };

  const handleQuestionOptionChange = (value: string) => {
    resetErrorMessages();
    const currentQuestion = wasteCategoryQuestionsAndOptions!.find((question) =>
      question.options.includes(value),
    )!;

    if (currentQuestion !== undefined) {
      currentQuestion.response = value;
      currentQuestion.parentQuestion === 'What surface is it on?'
        ? updateSurfacedetails(value)
        : currentQuestion.parentQuestion === 'Is it offensive?'
          ? updateOffensive(value === 'Yes' ? true : false)
          : currentQuestion.parentQuestion === 'Has the issue happened before?'
            ? updateIsRecurringIssue(value === 'Yes' ? true : false)
            : updateActualMeasurementDetails(value);

      if (currentQuestion.parentQuestion === 'Has the issue happened before?') {
        showIsShowWhenHappenedField(value === 'Yes' ? true : false);
      }
    } else {
      const currentQuestion = renderedWasteCategoryQuestionsAndOptions?.find(
        (tick) => tick.options.includes(value),
      )!;
      currentQuestion.response = value;
      currentQuestion.parentQuestion === 'What surface is it on?'
        ? updateSurfacedetails(value)
        : updateActualMeasurementDetails(value);
      currentQuestion.parentQuestion === 'Is it offensive?'
        ? updateOffensive(value === 'Yes' ? true : false)
        : currentQuestion.parentQuestion === 'Has the issue happened before?'
          ? updateIsRecurringIssue(value === 'Yes' ? true : false)
          : updateActualMeasurementDetails(value);

      if (currentQuestion.parentQuestion === 'Has the issue happened before?') {
        showIsShowWhenHappenedField(value === 'Yes' ? true : false);
      }
    }

    const renderedList = renderedWasteCategoryQuestionsAndOptions ?? [];
    const parentQuestion = currentQuestion?.parentQuestion;

    if (parentQuestion) {
      const storeMatchIndex = renderedList.findIndex(
        (q) => q.parentQuestion === parentQuestion,
      );
      if (
        storeMatchIndex !== -1 &&
        renderedList[storeMatchIndex] !== currentQuestion
      ) {
        updateRenderedWasteCategoryQuestionsAndOptions(
          renderedList.map((q, i) =>
            i === storeMatchIndex ? { ...q, response: value } : q,
          ),
        );
      }
    }

    // Don't re-render question when a field is clicked in an already rendered question
    if (
      renderedWasteCategoryQuestionsAndOptions?.indexOf(currentQuestion) === -1
    ) {
      return;
    }

    // list of questions without the already rendered questions
    wasteCategoryQuestionsAndOptions.splice(
      wasteCategoryQuestionsAndOptions.indexOf(currentQuestion),
      1,
    );

    if (wasteCategoryQuestionsAndOptions.length > 0) {
      updateRenderedWasteCategoryQuestionsAndOptions([
        ...renderedWasteCategoryQuestionsAndOptions!,
        wasteCategoryQuestionsAndOptions[0],
      ]);
    } else {
      // No more questions to reveal — still persist so the answer survives
      // navigation away and back (in-place mutation alone isn't stored).
      updateRenderedWasteCategoryQuestionsAndOptions([
        ...renderedWasteCategoryQuestionsAndOptions!,
      ]);
    }
  };

  const handlePrevious = () => {
    updateIsNavigate(true);
    navigate(RoutesList.UPLOAD_PHOTO);
  };

  const handleNext = () => {
    try {
      if (isImageMismatch) {
        inferCategoryValidation.parse({
          infercategoryName: selectedOption,
        });
      }
      if (currentSubcategory && currentSubcategory?.length > 1) {
        wasteCurrentSubcategory.parse({
          subcategoryName: selectedSubcategory,
        });
      }
      renderedWasteCategoryQuestionsAndOptions?.forEach((renderedQuestion) => {
        validateDynamicFields(
          renderedQuestion?.parentQuestion,
          renderedQuestion?.response,
        ).parse({
          [renderedQuestion.parentQuestion]: renderedQuestion.response,
        });
      });

      if (isRecurringIssue) {
        textAreaInputSchema({
          fieldName: 'whenRecurringHappened',
          requiredMessage: 'Enter details about the issue.',
          maxLength: extraQuestionData.textArea.maxLimit,
          maxLengthMessage: `The text must be less than ${extraQuestionData.textArea.maxLimit} characters`,
        }).parse({
          whenRecurringHappened: whenRecurringHappened || '',
        });
      }

      updateIsNavigate(true);
      navigate(RoutesList.WASTE_LOCATION);
    } catch (error: any) {
      const { errorPaths, errorMessages } = errorFormatter(error);
      setErrorMessages(errorMessages);
      setErrorPaths(errorPaths);
    }
  };

  useEffect(() => {
    updateIsNavigate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategory]);

  useEffect(() => {
    // Do not pre-populate the subcategory/questions when the uploaded image
    // suggests a different category than the one chosen from the URL. Wait for
    // the user to confirm the correct category in the confirm-category radio.
    const hasUnconfirmedImageMismatch =
      !!formattedInferCategory &&
      formattedInferCategory !== wasteCategory &&
      selectedOptionRef === '';

    if (
      renderedWasteCategoryQuestionsAndOptions === undefined &&
      !hasUnconfirmedImageMismatch
    ) {
      findSubcategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasteCategory]);

  // render question automatically if subcategory is only one.
  useEffect(() => {
    if (currentSubcategory && currentSubcategory?.length === 1) {
      findQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubcategory]);

  const subcategoryRef = useRef<RefObject<HTMLInputElement>[]>(
    currentSubcategory!.map(() => createRef<HTMLInputElement>()),
  );

  useEffect(() => {
    for (let i = 0; i < subcategoryRef.current.length; i++) {
      if (subcategoryRef?.current[i].current?.id === selectedSubcategory) {
        subcategoryRef?.current[i].current?.click();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    renderedWasteCategoryQuestionsAndOptions?.forEach((option) => {
      const el = document.getElementById(
        `${option.response}`,
      ) as HTMLInputElement;

      if (el) {
        el.click();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedWasteCategoryQuestionsAndOptions]);

  const formattedWasteCategory = (category: string) => {
    if (category !== undefined && category)
      return (category?.charAt(0)!.toUpperCase() +
        category?.replaceAll('_', ' ')?.slice(1)) as string;
  };

  const getSelectedAndInferCategory = (category: string) => {
    const unFormattedCategory = mainCategories.find(
      (mainCategory) => mainCategory === category,
    );
    return formattedWasteCategory(unFormattedCategory!);
  };

  const userCategory = getSelectedAndInferCategory(wasteCategory);

  const inferCategory = getSelectedAndInferCategory(formattedInferCategory!);

  useEffect(() => {
    if (inferCategory !== userCategory && inferCategory !== undefined) {
      updateIsImageMismatch(true);
    } else {
      updateIsImageMismatch(false);
    }
  }, [inferCategory, userCategory]);

  const labelText = () => {
    if (userCategory || !isImageMismatch) {
      return `Category: ${!isImageMismatch ? userCategory : selectedOption}`;
    }
  };

  const userCategoryRef = useRef<HTMLInputElement | null>(null);
  const inferCategoryRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (userCategoryRef?.current?.id === selectedOptionRef) {
      userCategoryRef?.current?.click();
    }
    if (inferCategoryRef?.current?.id === selectedOptionRef) {
      inferCategoryRef?.current?.click();
    }
    if (userCategoryRef?.current?.id.length! > 0) {
      updateCurrentSubcategory([
        {
          name: '',
          parentCategory: '',
          parentCategoryLabel: '',
        },
      ]);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      updateIsNavigate(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorMessages = (key: string) => {
    return errorMessages.filter((msg) => msg.path === key);
  };

  const handleChangeExtraText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    resetErrorMessages();
    updateWhenRecurringHappened(e.target.value);
  };

  return (
    <WasteDetailsTemplate
      formTitle="Details of the issue"
      buttonNext={{ label: 'Next', onClick: handleNext }}
      buttonPrevious={{ label: 'Previous', onClick: handlePrevious }}
    >
      <ErrorSummary
        messages={errorMessages.filter(
          (msg) => msg.path !== extraQuestionData.textArea.errorPath,
        )}
      />
      <Label
        text={labelText()}
        className="text-gray mb-4 text-xl"
        isTitle={false}
      />
      {filename && isImageMismatch && (
        <RadioOptions
          title="Please confirm the correct category"
          onChange={(value) => {
            handleInferCategoryOptionChange(value);
          }}
          options={[
            {
              labelText: inferCategory!,
              id: inferCategory,
              buttonRef: inferCategoryRef,
            },
            {
              labelText: userCategory!,
              id: userCategory,
              buttonRef: userCategoryRef,
            },
          ]}
          errorPath={errorPaths.find((path) => path === 'infercategoryName')}
          errorMessages={
            errorPaths.includes('infercategoryName') ? errorMessages : []
          }
        />
      )}
      {currentSubcategory !== undefined && currentSubcategory.length > 1 && (
        <RadioOptions
          title={currentSubcategory![0].parentCategoryLabel}
          onChange={(value) => {
            handleSubcategoryOptionChange(value);
          }}
          options={currentSubcategory!.map((option, index) => ({
            labelText: option.name,
            id: option.name,
            buttonRef: subcategoryRef.current![index],
          }))}
          errorPath={errorPaths.find((path) => path === 'subcategoryName')}
          errorMessages={
            errorPaths.includes('subcategoryName') ? errorMessages : []
          }
        />
      )}
      {currentSubcategory !== undefined &&
        renderedWasteCategoryQuestionsAndOptions?.map((item, index) =>
          item.options.length <= 5 ? (
            <RadioOptions
              key={index + item.parentQuestion}
              title={item.parentQuestion}
              onChange={(value) => {
                handleQuestionOptionChange(value);
              }}
              options={item.options.map((option) => ({
                labelText: option,
                id: option,
                buttonRef: null,
                isDefault: option === item.response,
              }))}
              errorPath={errorPaths.find(
                (path) => path === item.parentQuestion,
              )}
              errorMessages={
                errorPaths.includes(item.parentQuestion) ? errorMessages : []
              }
            />
          ) : (
            <SelectInput
              title={item.parentQuestion}
              key={index + item.parentQuestion}
              dataTestId="type-of-issue-select"
              defaultOption={item.response}
              options={item.options.map((option) => ({
                label: option,
                value: option,
              }))}
              placeholderText={
                item.parentQuestion === 'What surface is it on?'
                  ? surfaceDetails
                  : actualMeasurementDetails
              }
              id={`${item.parentQuestion}`}
              onChange={(value) => {
                handleQuestionOptionChange(value.value);
              }}
              errorPath={errorPaths.find(
                (path) => path === item.parentQuestion,
              )}
              errorMessages={
                errorPaths.includes(item.parentQuestion) ? errorMessages : []
              }
            />
          ),
        )}

      {isShowWhenHappenedField ? (
        <TextAreaTemplate
          id={extraQuestionData.textArea.id}
          labelText={extraQuestionData.textArea.title}
          description={extraQuestionData.textArea.description}
          errorPath={extraQuestionData.textArea.errorPath}
          errorMessages={getErrorMessages(extraQuestionData.textArea.id)}
          onChange={handleChangeExtraText}
          maxLength={extraQuestionData.textArea.maxLimit}
          initialTextValue={whenRecurringHappened}
          isLabelTextTitle={false}
        />
      ) : null}
    </WasteDetailsTemplate>
  );
};
